import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { UserCircle, Plus, X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssignmentManagement = () => {
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [assignments, setAssignments] = useState([]); // [{operator_id, user_id}]
  const [loading, setLoading] = useState(true);
  const [selectedOperator, setSelectedOperator] = useState({}); // per-user

  const load = useCallback(async () => {
    try {
      const [u, o, a] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/superadmin/operators`, { withCredentials: true }),
        axios.get(`${API_URL}/api/superadmin/assignments`, { withCredentials: true }),
      ]);
      setUsers(u.data);
      setOperators(o.data);
      setAssignments(a.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const opsById = Object.fromEntries(operators.map(o => [o.id, o]));
  const userAssignments = (uid) => assignments
    .filter(a => a.user_id === uid)
    .map(a => opsById[a.operator_id])
    .filter(Boolean);

  const handleAssign = async (user_id) => {
    const operator_id = selectedOperator[user_id];
    if (!operator_id) return;
    try {
      await axios.post(
        `${API_URL}/api/superadmin/assignments`,
        { operator_id, user_id },
        { withCredentials: true }
      );
      setSelectedOperator(prev => ({ ...prev, [user_id]: '' }));
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleUnassign = async (operator_id, user_id) => {
    try {
      await axios.delete(
        `${API_URL}/api/superadmin/assignments`,
        { data: { operator_id, user_id }, withCredentials: true }
      );
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to unassign');
    }
  };

  if (loading) return <div className="text-center py-8">Loading assignments...</div>;

  return (
    <div className="space-y-4" data-testid="assignment-management">
      <div>
        <h2 className="text-xl font-heading font-semibold">Assignments</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Assign operators to specific users. Operators only see the support threads of
          users assigned to them. Superadmin always has access to every thread.
        </p>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12 border border-border rounded-lg">
          <UserCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No users yet</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {users.map((u) => {
            const assigned = userAssignments(u.id);
            const remaining = operators.filter(
              o => o.is_active && !assigned.some(a => a.id === o.id)
            );
            return (
              <div
                key={u.id}
                className="border border-border rounded-lg p-4 space-y-3"
                data-testid={`assignment-user-${u.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {u.display_name || u.name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      No. {u.member_no != null ? String(u.member_no).padStart(3, '0') : '—'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-w-[60%] justify-end">
                    {assigned.length === 0 && (
                      <span className="text-xs text-muted-foreground italic">No operators assigned</span>
                    )}
                    {assigned.map(op => (
                      <Badge
                        key={op.id}
                        variant="secondary"
                        className="pl-2 pr-1 py-0.5 gap-1"
                        data-testid={`assigned-op-${op.id}-${u.id}`}
                      >
                        {op.display_name || op.name}
                        <button
                          onClick={() => handleUnassign(op.id, u.id)}
                          className="hover:bg-destructive/20 rounded p-0.5"
                          aria-label="Unassign"
                          data-testid={`unassign-${op.id}-${u.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
                {remaining.length > 0 && (
                  <div className="flex gap-2 items-center">
                    <Select
                      value={selectedOperator[u.id] || ''}
                      onValueChange={(v) => setSelectedOperator(prev => ({ ...prev, [u.id]: v }))}
                    >
                      <SelectTrigger className="flex-1" data-testid={`select-operator-${u.id}`}>
                        <SelectValue placeholder="Select operator to assign..." />
                      </SelectTrigger>
                      <SelectContent>
                        {remaining.map(op => (
                          <SelectItem key={op.id} value={op.id}>
                            {op.display_name || op.name} ({op.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => handleAssign(u.id)}
                      disabled={!selectedOperator[u.id]}
                      data-testid={`assign-button-${u.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Assign
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignmentManagement;
